import { useRef } from 'react';
import Draggable from 'react-draggable';
import { motion } from 'framer-motion';
import { Box, Fab, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconRobot } from '@tabler/icons-react';

import { useDispatch, useSelector } from 'store';
import { openChatbot } from 'store/slices/chatbotsSlice';
import { permissions } from 'auth/config';
import { hasPermission } from 'auth/permissionUtils';
import useAuth from 'hooks/useAuth';

import ChatbotDrawer from './ChatbotDrawer';

// ==============================|| CHATBOT LAUNCHER ||============================== //
//
// Floating action button pinned to the right edge at top: 25% (the Live
// Customize gear now sits at the very bottom instead). Left menu and main
// content stay in place; the drawer overlays from the right when opened.
//
// The button is larger with a colorful gradient and a floating + pulsing
// animation so it is easy to notice, and it can be dragged anywhere on screen.

const FAB_SIZE = 64;
// Movement (px) beyond which a pointer interaction counts as a drag, not a
// click. Guards against micro-movements swallowing an intended click.
const DRAG_THRESHOLD = 5;

const ChatbotLauncher = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { role } = useAuth();
  const { open } = useSelector((state) => state.chatbots);

  // nodeRef avoids findDOMNode (deprecated in StrictMode). dragStartRef and
  // draggedRef let us tell a real drag apart from a click (measured against
  // DRAG_THRESHOLD) so a click that jitters slightly still opens the drawer.
  const nodeRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);

  // Only roles granted USE_CHATBOT may see or open the assistant. Placed after
  // all hooks so the early return doesn't violate the rules of hooks.
  if (!hasPermission(role, permissions.USE_CHATBOT)) {
    return null;
  }

  const handleClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    dispatch(openChatbot());
  };

  return (
    <>
      {!open && (
        <Draggable
          nodeRef={nodeRef}
          bounds="body"
          onStart={(_event, data) => {
            dragStartRef.current = { x: data.x, y: data.y };
            draggedRef.current = false;
          }}
          onDrag={(_event, data) => {
            const dx = data.x - dragStartRef.current.x;
            const dy = data.y - dragStartRef.current.y;
            if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
              draggedRef.current = true;
            }
          }}
        >
          <Box
            ref={nodeRef}
            sx={{
              position: 'fixed',
              // pinned at the right edge, 25% down the viewport
              top: '25%',
              right: 10,
              zIndex: 1201,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <Tooltip title="Ask the assistant" placement="left">
              <motion.div
                // gentle floating bob to draw the eye
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                // no whileTap: a tap-scale would fire on drag start and shrink
                // the button mid-drag; hover scale is enough click feedback
                whileHover={{ scale: 1.1 }}
                style={{ position: 'relative', width: FAB_SIZE, height: FAB_SIZE }}
              >
                {/* expanding halo ring to signal interactivity */}
                <motion.span
                  animate={{ scale: [1, 1.7], opacity: [0.55, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    pointerEvents: 'none'
                  }}
                />
                <Fab
                  aria-label="open chatbot"
                  onClick={handleClick}
                  sx={{
                    width: FAB_SIZE,
                    height: FAB_SIZE,
                    color: theme.palette.primary.contrastText,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: theme.customShadows?.primary,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                    }
                  }}
                >
                  <IconRobot size={30} />
                </Fab>
              </motion.div>
            </Tooltip>
          </Box>
        </Draggable>
      )}
      <ChatbotDrawer />
    </>
  );
};

export default ChatbotLauncher;
